import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Coffee } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(username.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Přihlášeno");
      navigate("/", { replace: true });
    } else {
      toast.error(res.error || "Přihlášení selhalo");
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-[#F9F6F0]"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {/* Left - form */}
      <div className="flex flex-col justify-center px-8 sm:px-14 lg:px-20 py-12">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl bg-[#3A2D27] text-[#D4A373] grid place-items-center">
              <Coffee className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
                Caféčko
              </div>
              <div className="label-eyebrow">Výrobní ERP systém</div>
            </div>
          </div>

          <div className="label-eyebrow mb-3">Vítejte zpět</div>
          <h1
            className="text-4xl sm:text-5xl font-semibold tracking-tight mb-3 text-[#1C1917]"
            style={{ fontFamily: "Outfit" }}
          >
            Přihlášení do systému
          </h1>
          <p className="text-[#57534E] leading-relaxed mb-10">
            Zadejte své přihlašovací údaje. Pokud nemáte účet, požádejte administrátora.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-sm font-medium text-[#1C1917] block mb-1.5">Uživatelské jméno</label>
              <input
                data-testid="login-username-input"
                type="text"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="input-cafe w-full"
                placeholder="např. admin"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1C1917] block mb-1.5">Heslo</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-cafe w-full"
                placeholder="••••••••"
              />
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="btn-primary w-full disabled:opacity-60"
            >
              {submitting ? "Přihlašuji…" : "Přihlásit se"}
            </button>
          </form>

          <p className="text-xs text-[#57534E] mt-10">© Caféčko 2026 · Sklad · Výroba · Expedice</p>
        </div>
      </div>

      {/* Right - hero image */}
      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1447933601403-0c6688de566e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjb2ZmZWUlMjBiZWFucyUyMHJvYXN0aW5nfGVufDB8fHx8MTc4MDMzMjk0M3ww&ixlib=rb-4.1.0&q=85"
          alt="Pražení kávových zrn"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/80 via-[#1C1917]/20 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="label-eyebrow mb-3" style={{ color: "#D4A373" }}>
            Od zrna po pobočku
          </div>
          <h2 className="text-3xl font-semibold tracking-tight max-w-md" style={{ fontFamily: "Outfit" }}>
            Sledujte každou šarži — od příjmu surovin přes výrobu až po expedici na pobočky.
          </h2>
        </div>
      </div>
    </div>
  );
}
